import { FC, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { NUDGE_LIFETIME_MS } from './helper/diagnosticNudgeHelper';

/**
 * Room the bubble needs to the right of the widget: its 260px body, the 4px borders and
 * the 16px gap, plus a little breathing space before the viewport edge.
 */
export const NUDGE_OUTSIDE_SPACE_PX = 300;

interface Props {
  text: string;
  /** Called when the message closes itself (timeout) or the teacher clicks away. */
  onDismiss: () => void;
  /** Positioning is left to the caller so the bubble can be anchored per design. */
  className?: string;
}

/**
 * Ephemeral speech bubble anchored next to the diagnostic widget. It closes after
 * NUDGE_LIFETIME_MS or on the first click outside of it — whichever comes first —
 * and is never dismissible any other way (there is no close button by design).
 */
const DiagnosticWidgetNudge: FC<Props> = ({ text, onDismiss, className = '' }) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  // Kept in a ref so re-renders from a new `onDismiss` identity don't restart the
  // 10s countdown or re-register the outside-click listener.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const timeout = setTimeout(() => onDismissRef.current(), NUDGE_LIFETIME_MS);

    const handleClickOutside = (event: MouseEvent) => {
      if (!bubbleRef.current?.contains(event.target as Node)) {
        onDismissRef.current();
      }
    };

    // `capture` so the bubble still closes when the click lands on something that
    // stops propagation (e.g. the chart canvas or a PrimeReact button).
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  return (
    <motion.div
      ref={bubbleRef}
      role="status"
      initial={{ opacity: 0, scale: 0, rotateY: -90 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      transition={{ duration: 1, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
      className={`tw-w-[260px] tw-flex tw-flex-col tw-px-4 tw-py-6 tw-bg-stone-0 tw-border-solid tw-border-[4px] tw-border-neutral-100 tw-rounded-3xl tw-z-[3] ${className}`}
    >
      <h3 className="tw-font-atkinson tw-italic tw-text-lg tw-font-normal tw-text-neutral-950 tw-text-center tw-m-0">
        {text}
      </h3>
      {/* Tail pointing back at the widget. A rotated square with only the two
          outward edges bordered reads as a triangle flush against the bubble. */}
      <span className="tw-absolute tw-left-[-12px] tw-bottom-8 tw-h-4 tw-w-4 tw-rotate-45 tw-bg-stone-0 tw-border-solid tw-border-[4px] tw-border-neutral-100 tw-border-t-0 tw-border-r-0" />
    </motion.div>
  );
};

export default DiagnosticWidgetNudge;
